// Loads the shapes of saved areas. One cached loader for the whole app: results are kept per area (and per
// list of ids) for the life of the page, and a request already in flight is shared, so a grid of cards or two
// pages asking for the same area make one request. By-ids requests are queued so a grid never floods the
// maps API. A failure is NOT kept: the promise rejects, and the next call asks again, so one early failure (a
// token not ready yet, a busy API) cannot blank an area's outline until a reload.
// The maps API is passed in, so tests use a fake.
import { areaLookup } from '../../utils/areaLookup';
import type { CountryCode, MapType } from '../../config/countries';
import { readShape } from './readShape';
import type { AreaCell, AreaLike } from './types';

/** The part of the maps API the loader uses. */
export interface ShapesApi {
  getGeosByIds(
    type: MapType,
    ids: string[],
    country: CountryCode,
    catalogue?: boolean,
  ): Promise<{ results?: Array<{ geo_id: string | number; geometry?: unknown; name?: string | null; size_m?: number; parents?: string[] }> }>;
}

export interface LoadOptions {
  /** Ask for at most this many ids (thumbnails draw an outline, not every cell). */
  maxIds?: number;
}

export interface AreaShapeLoader {
  /**
   * The shapes of one saved area: its stored shape, or the shapes of its ids from the maps API. Rejects when
   * the request fails or the area's country cannot be told; an area with nothing to draw gives [].
   */
  load(area: AreaLike, options?: LoadOptions): Promise<AreaCell[]>;
  /** Forget every cached area and any queued request. */
  reset(): void;
}

/** The most by-ids requests in flight at once. */
const MAX_IN_FLIGHT = 3;

export const NO_COUNTRY_MESSAGE = "The app can't tell which country this area is in, so its shapes can't be loaded.";

export function createAreaShapeLoader(api: ShapesApi): AreaShapeLoader {
  const cache = new Map<string, Promise<AreaCell[]>>();
  let inFlight = 0;
  let waiting: Array<() => void> = [];

  const acquire = (): Promise<void> => {
    if (inFlight < MAX_IN_FLIGHT) {
      inFlight++;
      return Promise.resolve();
    }
    return new Promise((resolve) => waiting.push(resolve));
  };
  const release = (): void => {
    const next = waiting.shift();
    if (next) next(); // the slot passes straight to the next request
    else inFlight--;
  };

  async function fetchCells(area: AreaLike, maxIds: number | undefined): Promise<AreaCell[]> {
    const ids = area.ids;
    if (!ids?.length) return [];
    const lookup = areaLookup({ type: area.type, ids, country_code: area.country_code });
    if (!lookup) throw new Error(NO_COUNTRY_MESSAGE);

    await acquire();
    try {
      const response = await api.getGeosByIds(lookup.type, maxIds ? ids.slice(0, maxIds) : ids, lookup.country, lookup.catalogue);
      return (response.results ?? []).flatMap((r) => {
        const geometry = readShape(r.geometry);
        if (!geometry) return [];
        return [{
          geo_id: String(r.geo_id),
          geometry,
          ...(r.name != null ? { name: r.name } : {}),
          ...(typeof r.size_m === 'number' ? { size_m: r.size_m } : {}),
          ...(r.parents ? { parents: r.parents } : {}),
        }];
      });
    } finally {
      release();
    }
  }

  return {
    load(area, options = {}) {
      const { maxIds } = options;
      if (area.geometry) {
        // A stored shape is read on the spot and not cached: it may have just been edited under the same id.
        const geometry = readShape(area.geometry);
        return Promise.resolve(geometry ? [{ geo_id: area.id, geometry }] : []);
      }
      const key = [area.id, area.type, area.country_code ?? '', maxIds ?? '', area.ids?.join(',') ?? ''].join('|');
      let found = cache.get(key);
      if (!found) {
        const mine: Promise<AreaCell[]> = fetchCells(area, maxIds);
        // A failure is not kept; callers sharing this request all see it.
        mine.catch(() => {
          if (cache.get(key) === mine) cache.delete(key);
        });
        found = mine;
        cache.set(key, found);
      }
      return found;
    },
    reset() {
      cache.clear();
      waiting = [];
      inFlight = 0;
    },
  };
}
